import { WealthShell } from "@/pages/viveka/wealth/WealthShell";
import { cr, pct, useWealthPosition } from "@/lib/wealth";
import { EmptyState } from "@/components/shared";

const Row = ({ label, value, strong, testid, tone = "text-navy" }) => (
  <div className="flex items-center justify-between gap-4 px-6 py-3" data-testid={testid}>
    <p className={`text-sm ${strong ? "font-semibold text-navy" : "text-foreground/80"}`}>{label}</p>
    <p className={`font-mono tabular-nums ${strong ? "text-base font-semibold" : "text-sm"} ${tone}`}>{value}</p>
  </div>
);

export default function WealthLiquidity() {
  const { position } = useWealthPosition();
  const t = position?.totals;

  return (
    <WealthShell
      title="Liquidity & consolidated balance sheet"
      description="The same assets read by accessibility. Family assets less liabilities equals family net worth."
    >
      {!t ? (
        <EmptyState testid="liquidity-empty" title="No records yet"
                    message="Add assets and liabilities in the register to see the balance sheet." />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="wealth-liquidity">
          <div className="panel overflow-hidden" data-testid="liquidity-bands">
            <p className="border-b border-border px-6 py-4 font-display text-lg text-navy">
              Family wealth by accessibility
            </p>
            <div className="divide-y divide-border">
              {position.liquidity.map((li) => (
                <div key={li.key} className="px-6 py-4" data-testid={`liquidity-band-${li.key}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-navy">{li.label}</p>
                      <p className="text-[11px] text-muted-foreground">{li.definition}</p>
                    </div>
                    <div className="flex items-center gap-5">
                      <p className="font-mono text-sm tabular-nums text-foreground/80">{cr(li.value)}</p>
                      <p className="w-20 text-right font-mono text-sm font-semibold tabular-nums text-navy">{pct(li.pct)}</p>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-teal" style={{ width: `${li.pct || 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border bg-teal/5 px-6 py-4" data-testid="liquidity-accessible-90">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-navy">Accessible within 90 days</p>
                <p className="font-mono text-base font-semibold tabular-nums text-teal">
                  {cr(position.accessible_90_days.value)} · {pct(position.accessible_90_days.pct)}
                </p>
              </div>
            </div>
          </div>

          <div className="panel overflow-hidden" data-testid="balance-sheet">
            <p className="border-b border-border px-6 py-4 font-display text-lg text-navy">
              Consolidated family balance sheet
            </p>
            <div className="divide-y divide-border">
              <Row testid="bs-gross" label="Gross asset value" value={cr(t.gross_assets)} />
              <Row testid="bs-linked" label="Less: asset-linked liabilities"
                   value={`(${cr(t.asset_linked_liabilities)})`} tone="text-destructive" />
              <Row testid="bs-net-assets" label="Net asset value" value={cr(t.net_assets)} strong />
              <Row testid="bs-standalone" label="Less: standalone liabilities"
                   value={`(${cr(t.standalone_liabilities)})`} tone="text-destructive" />
              <Row testid="bs-net-worth" label="Total family net worth" value={cr(t.net_worth)} strong />
            </div>
            <div className="border-t border-border bg-muted/20 px-6 py-4">
              <p className="label-caps">Provenance</p>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Calculated from {t.asset_count} active assets as at {(position.as_at || "").slice(0, 10)}.
                Every figure drills down to the asset record, its valuation date and its source.
              </p>
              {position.stale_assets?.length > 0 && (
                <p className="mt-2 text-[11px] font-semibold text-[hsl(28_82%_38%)]"
                   data-testid="liquidity-stale-note">
                  Stale valuations: {position.stale_assets.map((a) => a.name).join(", ")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </WealthShell>
  );
}
